import {Alert, Button, Typography} from "@mui/material";
import {ApiError} from "@variocube/app-ui";
import React, {useEffect} from "react";
import {useAsyncCallback} from "react-async-hook";
import {useBooking} from "../../context/booking";
import {B2Booking} from "../../data/client.gen";
import {AdmissionErrorCode, useSiteApi} from "../../data/sites";
import {useLocalization} from "../../i18n";

// Long enough for the user to read "Entrance opened." before the alert reverts to the idle prompt; a UX call, not load-bearing.
const SUCCESS_TIMEOUT_MS = 5000;

export function OpenAdmissionButton({booking}: { booking: B2Booking }) {
	const {t} = useLocalization();
	const {isBookingActive} = useBooking();
	const {openAdmission} = useSiteApi();

	const {site} = booking;

	const disabledOutsideWindow = site.requireActiveBookingForAdmission && !isBookingActive;

	const {loading, error, result, execute, reset} = useAsyncCallback(async () => {
		await openAdmission(site.id, booking.id);
		return true;
	});

	// Auto-clear the success state so a stale "Entrance opened." alert doesn't linger after the
	// booking window expires or the user wanders away from the page. Errors stick until retry.
	useEffect(() => {
		if (!result) {
			return;
		}
		const timer = window.setTimeout(reset, SUCCESS_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [result, reset]);

	if (!site.admissionAvailable) {
		return null;
	}

	const severity = error ? "error" : (result ? "success" : (disabledOutsideWindow ? "warning" : "info"));

	return (
		<Alert
			severity={severity}
			action={
				<Button
					sx={{minWidth: 120}}
					size="small"
					variant="contained"
					color="primary"
					disableElevation
					disabled={loading || disabledOutsideWindow}
					onClick={() => execute()}
				>
					{t("booking.admission.button")}
				</Button>
			}
		>
			<Typography variant="body2">{renderMessage()}</Typography>
		</Alert>
	);

	function renderMessage() {
		if (error) {
			if (error instanceof ApiError) {
				switch (error.code as AdmissionErrorCode | undefined) {
					case "BOOKING_REQUIRED":
						return t("booking.admission.error.bookingRequired");
					case "OUTSIDE_HOURS":
						return t("booking.admission.error.outsideHours");
					case "RATE_LIMITED":
						return t("booking.admission.error.rateLimited");
				}
			}
			return t("booking.admission.error.generic");
		}
		if (result) {
			return t("booking.admission.success");
		}
		if (disabledOutsideWindow) {
			return t("booking.admission.disabledOutsideWindow");
		}
		return t("booking.admission.message");
	}
}
